import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { MdSend, MdPerson, MdCheckCircle } from 'react-icons/md';
import dailyReportService from '../../../services/dailyReportService';
import { useAuth } from '../../../contexts/AuthContext';

const ConversationBox = ({ reportId, conversation = [], legacyFeedback, isChairman, onSaved }) => {
  const { user } = useAuth();
  const [replyMessage, setReplyMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // Combine legacy feedback with new conversation for display
  const displayConversation = [...conversation];
  if (legacyFeedback && conversation.length === 0) {
    displayConversation.push({
      _id: 'legacy',
      senderRole: 'CHAIRMAN',
      senderName: 'Chairman',
      message: legacyFeedback,
      timestamp: new Date()
    });
  }

  const handleReply = async () => {
    const trimmed = replyMessage.trim();
    if (!trimmed) { toast.error('Message cannot be empty.'); return; }
    try {
      setSaving(true);
      const res = await dailyReportService.addReply(reportId, trimmed);
      if (res.success) {
        toast.success('Reply sent!');
        setReplyMessage('');
        onSaved && onSaved(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reply.');
    } finally {
      setSaving(false);
    }
  };

  const hasMessages = displayConversation.length > 0;
  
  // HR can only reply if Chairman has started the conversation
  const canReply = isChairman || hasMessages;

  if (!canReply && !hasMessages) {
    return null; // HR sees nothing if Chairman hasn't replied yet
  }

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border-light pt-4">
      {hasMessages && (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
            <MdCheckCircle size={14} className="text-accent-green" /> Conversation
          </p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
            {displayConversation.map((msg, idx) => {
              const isMine = msg.senderId === user?.userId || (msg.senderRole === user?.role);
              
              return (
                <div key={msg._id || idx} className={`flex flex-col max-w-[85%] ${isMine ? 'ml-auto items-end' : 'mr-auto items-start'}`}>
                  <span className="text-[10px] text-text-tertiary mb-1 flex items-center gap-1">
                    <MdPerson size={10} /> {msg.senderName} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <div className={`p-3 rounded-2xl text-sm ${
                    isMine 
                      ? 'bg-accent-green text-white rounded-tr-sm' 
                      : 'bg-background-secondary text-text-primary rounded-tl-sm border border-border-light'
                  }`}>
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {canReply && (
        <div className={`mt-2 p-3 rounded-xl border border-border-light bg-surface shadow-sm focus-within:border-accent-green/50 focus-within:ring-1 focus-within:ring-accent-green/20 transition-all ${hasMessages ? '' : 'border-accent-green/30 bg-accent-green/5'}`}>
          {!hasMessages && (
             <p className="text-xs font-semibold text-accent-green uppercase tracking-wider mb-2">
               Add Feedback
             </p>
          )}
          <textarea
            rows={hasMessages ? 2 : 3}
            className="w-full px-1 py-1 bg-transparent focus:outline-none text-sm text-text-primary placeholder-text-tertiary resize-none"
            placeholder={hasMessages ? "Type your reply..." : "Write your feedback for this HR report…"}
            maxLength={3000}
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
          />
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-light/50">
            <span className="text-[10px] text-text-tertiary">{replyMessage.length}/3000</span>
            <button
              type="button"
              onClick={handleReply}
              disabled={saving || !replyMessage.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-accent-green text-white font-semibold hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {saving ? (
                <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <MdSend size={12} />
              )}
              {saving ? 'Sending…' : (hasMessages ? 'Send Reply' : 'Send Feedback')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationBox;
